/**
 * ============================================================================
 * HOUSEHOLD.JS v29.1 - INFO BUTTON
 * ============================================================================
 *
 * ✅ v29.1: INFO BUTTON - Refresh Button entfernt, Info Button hinzugefügt
 *           - Info Modal mit Modul-Beschreibung
 *           - Konsistent mit anderen Modulen
 *
 * ✅ v29.0: MOBILE DESIGN GUIDE
 *           - isMobile State mit Resize-Listener
 *           - Responsive Container (padding: 0 auf Mobile)
 *           - Touch-Targets min 44px
 *           - Einheitliche Abstände (8px Basis)
 *
 * 🔧 v28.6: PHONE & EMAIL FIELDS
 *           - Phone und Email in Add/Edit Member
 *           - Backend sendet phone mit
 *
 * 🔧 v28.5: EDIT & DELETE MEMBERS
 *           - EditMemberModal zum Bearbeiten von Mitgliedern
 *           - Löschen mit Bestätigungsdialog
 *           - PUT/DELETE API Calls
 *
 * 🔧 v28.4: LOAD HOUSEHOLD MEMBERS
 *           - FIX: household_members werden jetzt beim Laden abgerufen
 *           - Daten bleiben nach Seiten-Refresh erhalten
 *           - GLOBAL_MEMBERS_CACHE für schnelles Laden
 *
 * 🔧 v28.3: INVITE CODE BUTTON
 *           - "Mit Code einladen" Button in FamilyMemberList
 *           - Modal zeigt Einladungscode an
 *           - Kopieren & Teilen Funktionen
 *           - Nur für Admins sichtbar
 *
 * 🔧 v28.2: ADD MEMBER MODAL
 *           - Modal zum Hinzufügen neuer Familienmitglieder
 *           - Kinder/Großeltern ohne eigenen Account
 *           - Name, Rolle, Farbe, optionale Felder
 *
 * 🔧 v28.1: KLICKBARE AKTIVITÄTEN
 *           - onNavigate Prop für Modul-Navigation
 *           - Klick auf Termine → Kalender
 *           - Klick auf Notizen → Notes
 *           - Klick auf Einkäufe → Shopping
 *           - Klick auf Aufgaben → Tasks
 *
 * 🔧 v28.0: FAMILY MEMBER REDESIGN
 *           - Neues Master-Detail Layout
 *           - Links: Familienmitglieder-Liste (FamilyMemberList)
 *           - Rechts: Detail-Ansicht pro Person (FamilyMemberDetail)
 *
 * ✅ v27.0: INVITE CODE REMOVED (Settings only)
 * ✅ v26.0: TOKEN CONTEXT
 * ✅ v25.0: ANALYTICS TRACKING
 *
 * @version 29.1.0 - Info Button
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToken } from '../../context/TokenContext'; // ✅ v26: NEU!
import { useTranslation } from '../../context/I18nContext';
import { useTheme } from '../../context/ThemeContext';
import Loading, { Skeleton } from '../common/Loading';
import FamilyMemberList from './FamilyMemberList';
import FamilyMemberDetail from './FamilyMemberDetail';
import AddMemberModal from './AddMemberModal'; // ✅ v28.2: NEU!
import EditMemberModal from './EditMemberModal'; // ✅ v28.5: NEU!
import './Household.css';

// ============================================================================
// ✅ v25: ANALYTICS + API CONFIG
// ============================================================================
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const LOADING_DELAY_MS = 200;

console.log('👥 Household.js v29.1 loaded - INFO BUTTON ⓘ');

/**
 * ✅ v25: Safe Track Function - nutzt /track/batch
 */
const trackAnalyticsEvent = async (token, eventName, metadata = {}) => {
  if (!token || !eventName) return;
  
  try {
    const event = {
      type: 'event',
      event_name: String(eventName).substring(0, 100),
      component: String(metadata.component || 'Household').substring(0, 100),
      metadata: metadata,
      timestamp: new Date().toISOString()
    };
    
    const response = await fetch(`${API_BASE_URL}/api/analytics/track/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ events: [event] })
    });
    
    if (response.ok) {
      console.log(`✅ Analytics tracked: ${eventName}`, metadata);
    } else {
      console.warn(`❌ Analytics failed: ${response.status}`, eventName);
    }
  } catch (err) {
    console.warn(`❌ Analytics error: ${eventName}`, err.message);
  }
};

// ============================================================================
// GLOBALER STATE + SHARED PROMISE
// ============================================================================
let GLOBAL_HOUSEHOLD_CACHE = null;
let GLOBAL_USERS_CACHE = [];
let GLOBAL_STATS_CACHE = null;
let GLOBAL_MEMBERS_CACHE = []; // ✅ v28: Familienmitglieder Cache
let GLOBAL_HAS_LOADED = false;
let GLOBAL_LOADING_PROMISE = null;

// ============================================================================
// COMPONENT
// ============================================================================
function Household({ active, onNavigate }) {
  // ✅ v26: useLogto NUR für isAuthenticated, NICHT für getAccessToken!
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  
  // ✅ v26: Token aus zentralem Context!
  const { token, loading: tokenLoading } = useToken();
  
  const { t } = useTranslation();
  const { isDark } = useTheme();

  // isMobile State ZUERST deklarieren (vor styles)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const styles = getStyles(isDark, isMobile);
  
  // ============================================================================
  // STATE
  // ============================================================================
  const [isLoading, setIsLoading] = useState(!GLOBAL_HAS_LOADED);
  const [showLoading, setShowLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(!GLOBAL_HAS_LOADED);
  const [error, setError] = useState(null);
  
  // Multi-User Sharing State
  const [household, setHousehold] = useState(() => GLOBAL_HOUSEHOLD_CACHE);
  const [householdUsers, setHouseholdUsers] = useState(() => GLOBAL_USERS_CACHE);
  const [stats, setStats] = useState(() => GLOBAL_STATS_CACHE);

  // ✅ v28: Family Member State
  const [familyMembers, setFamilyMembers] = useState(() => GLOBAL_MEMBERS_CACHE);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showMobileDetail, setShowMobileDetail] = useState(false);

  // ✅ v28.2: Add Member Modal State
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [addMemberLoading, setAddMemberLoading] = useState(false);

  // ✅ v28.5: Edit Member Modal State
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [editMemberLoading, setEditMemberLoading] = useState(false);
  const [memberToEdit, setMemberToEdit] = useState(null);

  // ✅ v28.3: Invite Code Modal State
  const [showInviteCodeModal, setShowInviteCodeModal] = useState(false);

  // Form States
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [householdName, setHouseholdName] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [showInfo, setShowInfo] = useState(false); // ✅ v29.1: Info Modal

  // ============================================================================
  // REFS
  // ============================================================================
  const loadingTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  const hasTrackedViewRef = useRef(false);

  // ============================================================================
  // ✅ v29: Mobile Responsive State
  // ============================================================================
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ============================================================================
  // ✅ v26: Track Mount/Unmount
  // ============================================================================
  useEffect(() => {
    isMountedRef.current = true;
    console.log('🔄 Household MOUNTED');

    return () => {
      isMountedRef.current = false;
      console.log('🔄 Household UNMOUNTING');
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, []);

  // ✅ v26: Analytics tracken wenn Token verfügbar (aus Context!)
  useEffect(() => {
    if (token && !hasTrackedViewRef.current && active) {
      hasTrackedViewRef.current = true;
      console.log('🔐 Household: Using token from TokenContext');
      trackAnalyticsEvent(token, 'component_view', { 
        component: 'Household',
        hasHousehold: !!GLOBAL_HOUSEHOLD_CACHE
      });
    }
  }, [token, active]);

  // ============================================================================
  // ✅ v26: ANALYTICS HELPER - Verwendet Token aus Context
  // ============================================================================
  const trackEvent = useCallback(async (eventName, metadata = {}) => {
    if (!token) {
      console.warn('❌ No token available for analytics');
      return;
    }
    
    trackAnalyticsEvent(token, eventName, {
      component: 'Household',
      ...metadata
    });
  }, [token]);

  // ============================================================================
  // Sync to Global Cache
  // ============================================================================
  useEffect(() => {
    GLOBAL_HOUSEHOLD_CACHE = household;
    if (householdUsers.length > 0) GLOBAL_USERS_CACHE = householdUsers;
    if (stats) GLOBAL_STATS_CACHE = stats;
    if (familyMembers.length > 0) GLOBAL_MEMBERS_CACHE = familyMembers;
  }, [household, householdUsers, stats, familyMembers]);

  // ✅ v28: Konvertiere householdUsers zu familyMembers Format
  useEffect(() => {
    if (householdUsers.length > 0) {
      const members = householdUsers.map((user, index) => ({
        id: user.id,
        name: user.display_name || user.email?.split('@')[0] || 'Unbekannt',
        role: user.is_owner ? 'Ersteller' : (user.role === 'admin' ? 'Admin' : 'Mitglied'),
        email: user.email,
        avatar: user.is_owner ? '👑' : (user.role === 'admin' ? '⭐' : null),
        color: getColorForIndex(index),
        // Platzhalter - können später aus erweiterten Profildaten kommen
        location: null,
        schedule: null,
        age: null
      }));
      setFamilyMembers(members);
    }
  }, [householdUsers]);

  /**
   * ✅ v28: Farbe basierend auf Index
   */
  function getColorForIndex(index) {
    const colors = ['#4A90A4', '#6BA893', '#A855F7', '#3B82F6', '#22C55E', '#F59E0B'];
    return colors[index % colors.length];
  }

  /**
   * ✅ v28: Mitglied auswählen Handler
   */
  const handleSelectMember = useCallback((member) => {
    setSelectedMember(member);
    setShowMobileDetail(true);
    trackEvent('family_member_selected', { member_id: member.id });
  }, [trackEvent]);

  /**
   * ✅ v28: Zurück zur Liste (Mobile)
   */
  const handleBackToList = useCallback(() => {
    setShowMobileDetail(false);
    // Delayed deselect for animation
    setTimeout(() => {
      if (!showMobileDetail) setSelectedMember(null);
    }, 300);
  }, [showMobileDetail]);

  /**
   * ✅ v28.3: Invite Code Modal öffnen
   */
  const handleInviteWithCode = useCallback(() => {
    setShowInviteCodeModal(true);
    trackEvent('invite_code_modal_opened', { household_id: household?.id });
  }, [trackEvent, household?.id]);

  /**
   * ✅ v28.3: Code kopieren
   */
  const handleCopyInviteCode = useCallback(async () => {
    if (!household?.invite_code) return;

    try {
      await navigator.clipboard.writeText(household.invite_code);
      setActionSuccess('Einladungscode kopiert!');
      trackEvent('invite_code_copied', { household_id: household?.id });
    } catch (err) {
      console.error('Copy failed:', err);
      setActionError('Kopieren fehlgeschlagen');
    }
  }, [household?.invite_code, household?.id, trackEvent]);

  /**
   * ✅ v28.3: Code teilen (Share API)
   */
  const handleShareInviteCode = useCallback(async () => {
    if (!household?.invite_code) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${household.name} - Einladung`,
          text: `Tritt unserem Haushalt "${household.name}" bei! Einladungscode: ${household.invite_code}`,
          url: window.location.origin
        });
        trackEvent('invite_code_shared', { household_id: household?.id });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else {
      // Fallback: Copy to clipboard
      handleCopyInviteCode();
    }
  }, [household, trackEvent, handleCopyInviteCode]);

  /**
   * ✅ v28.2: Mitglied hinzufügen Handler
   */
  const handleAddMember = useCallback(async (memberData) => {
    if (!token || !household?.id) return;

    setAddMemberLoading(true);
    setActionError(null);

    try {
      // API Call zum Erstellen des Mitglieds
      const response = await fetch(`${API_BASE_URL}/api/household/members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: memberData.name,
          role: memberData.role,
          color: memberData.color,
          phone: memberData.phone || null,
          email: memberData.email || null,
          avatar: memberData.role === 'kind' ? '👶' :
                  memberData.role === 'elternteil' ? '👨' :
                  memberData.role === 'grosseltern' ? '👴' : '👤',
          birth_date: memberData.birthDate || null,
          location: memberData.location || null,
          schedule: memberData.schedule || null,
          notes: null
        })
      });

      if (response.ok) {
        const result = await response.json();
        const newMember = result.member;
        console.log('✅ New member created:', newMember);

        // Füge neues Mitglied zur Liste hinzu
        const memberForList = {
          id: newMember.id,
          name: newMember.name,
          role: newMember.role === 'kind' ? 'Kind' :
                newMember.role === 'elternteil' ? 'Elternteil' :
                newMember.role === 'grosseltern' ? 'Großeltern' :
                newMember.role || 'Andere',
          color: newMember.color || memberData.color,
          phone: newMember.phone || memberData.phone || null,
          email: newMember.email || memberData.email || null,
          location: newMember.location || memberData.location || null,
          schedule: newMember.schedule || memberData.schedule || null,
          birth_date: newMember.birth_date || memberData.birthDate || null,
          age: null,
          avatar: newMember.avatar || (memberData.role === 'kind' ? '👶' :
                  memberData.role === 'elternteil' ? '👨' :
                  memberData.role === 'grosseltern' ? '👴' : '👤'),
          isMember: true
        };

        setFamilyMembers(prev => [...prev, memberForList]);
        setShowAddMemberModal(false);
        setActionSuccess(`${memberData.name} wurde hinzugefügt!`);
        trackEvent('family_member_added', { role: memberData.role });

        // Clear success message after 3 seconds
        setTimeout(() => setActionSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Fehler beim Hinzufügen');
      }
    } catch (err) {
      console.error('❌ Error adding member:', err);
      setActionError(err.message || 'Fehler beim Hinzufügen des Mitglieds');
    } finally {
      setAddMemberLoading(false);
    }
  }, [token, household?.id, trackEvent]);

  /**
   * ✅ v28.5: Mitglied bearbeiten öffnen
   */
  const handleOpenEditMember = useCallback((member) => {
    console.log('✏️ Opening edit modal for member:', member);
    setMemberToEdit(member);
    setShowEditMemberModal(true);
    trackEvent('edit_member_modal_opened', { member_id: member.id });
  }, [trackEvent]);

  /**
   * ✅ v28.5: Mitglied speichern (Update)
   */
  const handleEditMember = useCallback(async (memberData) => {
    if (!token || !memberData?.id) return;

    setEditMemberLoading(true);
    setActionError(null);

    try {
      console.log('📝 Updating member:', memberData.id, memberData);

      const response = await fetch(`${API_BASE_URL}/api/household/members/${memberData.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: memberData.name,
          role: memberData.role,
          color: memberData.color,
          phone: memberData.phone || null,
          email: memberData.email || null,
          birth_date: memberData.birth_date || memberData.birthDate || null,
          notes: memberData.notes || null,
          location: memberData.location || null,
          schedule: memberData.schedule || null,
          avatar: memberData.role === 'kind' ? '👶' :
                  memberData.role === 'elternteil' ? '👨' :
                  memberData.role === 'grosseltern' ? '👴' : '👤'
        })
      });

      if (response.ok) {
        const result = await response.json();
        const updatedMember = result.member;
        console.log('✅ Member updated:', updatedMember);

        // Update in der Liste
        setFamilyMembers(prev => prev.map(m => {
          if (m.id === memberData.id) {
            return {
              ...m,
              name: updatedMember.name,
              role: updatedMember.role === 'kind' ? 'Kind' :
                    updatedMember.role === 'elternteil' ? 'Elternteil' :
                    updatedMember.role === 'grosseltern' ? 'Großeltern' :
                    updatedMember.role || 'Andere',
              color: updatedMember.color || m.color,
              phone: updatedMember.phone,
              email: updatedMember.email,
              birth_date: updatedMember.birth_date,
              notes: updatedMember.notes,
              location: updatedMember.location,
              schedule: updatedMember.schedule,
              avatar: updatedMember.avatar || m.avatar
            };
          }
          return m;
        }));

        // Update selectedMember falls es das gleiche ist
        if (selectedMember?.id === memberData.id) {
          setSelectedMember(prev => ({
            ...prev,
            name: updatedMember.name,
            role: updatedMember.role === 'kind' ? 'Kind' :
                  updatedMember.role === 'elternteil' ? 'Elternteil' :
                  updatedMember.role === 'grosseltern' ? 'Großeltern' :
                  updatedMember.role || 'Andere',
            color: updatedMember.color || prev.color,
            phone: updatedMember.phone,
            email: updatedMember.email,
            birth_date: updatedMember.birth_date,
            notes: updatedMember.notes,
            location: updatedMember.location,
            schedule: updatedMember.schedule,
            avatar: updatedMember.avatar || prev.avatar
          }));
        }

        setShowEditMemberModal(false);
        setMemberToEdit(null);
        setActionSuccess(`${memberData.name} wurde aktualisiert!`);
        trackEvent('family_member_updated', { member_id: memberData.id });

        setTimeout(() => setActionSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Fehler beim Aktualisieren');
      }
    } catch (err) {
      console.error('❌ Error updating member:', err);
      setActionError(err.message || 'Fehler beim Aktualisieren des Mitglieds');
    } finally {
      setEditMemberLoading(false);
    }
  }, [token, trackEvent, selectedMember?.id]);

  /**
   * ✅ v28.5: Mitglied löschen
   */
  const handleDeleteMember = useCallback(async (memberId, memberName) => {
    if (!token || !memberId) return;

    setEditMemberLoading(true);
    setActionError(null);

    try {
      console.log('🗑️ Deleting member:', memberId, memberName);

      const response = await fetch(`${API_BASE_URL}/api/household/members/${memberId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        console.log('✅ Member deleted:', memberId);

        // Aus der Liste entfernen
        setFamilyMembers(prev => prev.filter(m => m.id !== memberId));

        // Falls das gelöschte Mitglied ausgewählt war, Auswahl aufheben
        if (selectedMember?.id === memberId) {
          setSelectedMember(null);
          setShowMobileDetail(false);
        }

        setShowEditMemberModal(false);
        setMemberToEdit(null);
        setActionSuccess(`${memberName} wurde entfernt!`);
        trackEvent('family_member_deleted', { member_id: memberId });

        setTimeout(() => setActionSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Fehler beim Löschen');
      }
    } catch (err) {
      console.error('❌ Error deleting member:', err);
      setActionError(err.message || 'Fehler beim Löschen des Mitglieds');
    } finally {
      setEditMemberLoading(false);
    }
  }, [token, trackEvent, selectedMember?.id]);

  // ============================================
  // ✅ v26: API HELPER - Verwendet Token aus Context
  // ============================================
  const apiCall = useCallback(async (endpoint, options = {}) => {
    if (!token) {
      throw new Error('Kein Token verfügbar');
    }
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || `Error ${response.status}`);
    }
    return data;
  }, [token]);

  // ============================================================================
  // ✅ v26: FETCH FUNCTION - Verwendet Token aus Context
  // ============================================================================
  const fetchDataFromBackend = useCallback(async () => {
    console.log('📡 Fetching household data from backend...');

    if (!token) {
      throw new Error('Kein Token verfügbar');
    }

    const householdData = await apiCall('/api/household');
    const fetchedHousehold = householdData.household;

    let fetchedUsers = [];
    let fetchedMembers = [];

    if (fetchedHousehold) {
      const usersData = await apiCall('/api/household/users');
      fetchedUsers = usersData.users || [];

      // ✅ v28.4: Lade auch household_members (Kinder, Großeltern ohne Account)
      try {
        const membersData = await apiCall('/api/household/members');
        fetchedMembers = (membersData.members || []).map(m => ({
          id: m.id,
          name: m.name,
          role: m.role === 'kind' ? 'Kind' :
                m.role === 'elternteil' ? 'Elternteil' :
                m.role === 'grosseltern' ? 'Großeltern' :
                m.role || 'Andere',
          color: m.color || '#3b82f6',
          avatar: m.avatar || '👤',
          phone: m.phone,
          email: m.email,
          birth_date: m.birth_date,
          notes: m.notes,
          location: m.location,
          schedule: m.schedule,
          isMember: true // Markierung: Kein echter User
        }));
        console.log('👨‍👩‍👧 Loaded household members:', fetchedMembers.length);
      } catch (err) {
        console.warn('⚠️ Could not load household members:', err.message);
      }
    }

    const fetchedStats = await apiCall('/api/household/stats');

    console.log('✅ Fetched household data:', {
      hasHousehold: !!fetchedHousehold,
      users: fetchedUsers.length,
      members: fetchedMembers.length,
      stats: !!fetchedStats
    });

    return {
      household: fetchedHousehold,
      users: fetchedUsers,
      members: fetchedMembers,
      stats: fetchedStats
    };
  }, [apiCall, token]);

  // ============================================================================
  // LOAD DATA
  // ============================================================================
  const loadData = useCallback(async (forceRefresh = false) => {
    if (!isAuthenticated) {
      setIsLoading(false);
      setIsInitialLoad(false);
      setError(t('household.notLoggedIn'));
      return;
    }
    
    // ✅ v26: Warten auf Token aus Context
    if (!token) {
      console.log('⏳ Waiting for token from TokenContext...');
      return;
    }
    
    if (GLOBAL_HAS_LOADED && !forceRefresh) {
      console.log('📦 Using cached household data (hasLoaded=true)');
      if (isMountedRef.current) {
        setHousehold(GLOBAL_HOUSEHOLD_CACHE);
        setHouseholdUsers(GLOBAL_USERS_CACHE);
        setStats(GLOBAL_STATS_CACHE);
        setIsLoading(false);
        setIsInitialLoad(false);
      }
      return;
    }
    
    if (GLOBAL_LOADING_PROMISE) {
      console.log('⏳ Waiting for existing load...');
      try {
        const result = await GLOBAL_LOADING_PROMISE;
        console.log('✅ Got data from shared promise');
        if (isMountedRef.current) {
          setHousehold(result.household);
          setHouseholdUsers(result.users);
          setStats(result.stats);
          setIsLoading(false);
          setIsInitialLoad(false);
        }
      } catch (err) {
        console.error('❌ Shared promise failed:', err);
        if (isMountedRef.current) {
          setError(t('household.errorLoading') + ': ' + err.message);
          setIsLoading(false);
          setIsInitialLoad(false);
        }
      }
      return;
    }
    
    console.log('🚀 Starting new load...');
    
    if (isMountedRef.current) {
      setIsLoading(true);
      setError(null);
      
      if (!isInitialLoad) {
        loadingTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setShowLoading(true);
          }
        }, LOADING_DELAY_MS);
      }
    }
    
    GLOBAL_LOADING_PROMISE = fetchDataFromBackend();
    
    try {
      const result = await GLOBAL_LOADING_PROMISE;

      GLOBAL_HOUSEHOLD_CACHE = result.household;
      GLOBAL_USERS_CACHE = result.users;
      GLOBAL_STATS_CACHE = result.stats;
      GLOBAL_MEMBERS_CACHE = result.members || []; // ✅ v28.4: Members cachen
      GLOBAL_HAS_LOADED = true;

      if (isMountedRef.current) {
        setHousehold(result.household);
        setHouseholdUsers(result.users);
        setStats(result.stats);
        setFamilyMembers(result.members || []); // ✅ v28.4: Members setzen
        setIsInitialLoad(false);
      }

    } catch (err) {
      console.error('❌ Load error:', err.message);

      trackEvent('household_load_error', { error: err.message });

      if (isMountedRef.current) {
        setError(t('household.errorLoading') + ': ' + err.message);
        setIsInitialLoad(false);

        if (GLOBAL_HOUSEHOLD_CACHE || GLOBAL_STATS_CACHE) {
          setHousehold(GLOBAL_HOUSEHOLD_CACHE);
          setHouseholdUsers(GLOBAL_USERS_CACHE);
          setStats(GLOBAL_STATS_CACHE);
          setFamilyMembers(GLOBAL_MEMBERS_CACHE); // ✅ v28.4: Members aus Cache
        }
      }
    } finally {
      GLOBAL_LOADING_PROMISE = null;
      
      if (isMountedRef.current) {
        setIsLoading(false);
        setShowLoading(false);
      }
      
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    }
  }, [isAuthenticated, isInitialLoad, fetchDataFromBackend, t, trackEvent, token]);

  // ============================================================================
  // INITIAL LOAD - ✅ v26: Wartet auf Token aus Context
  // ============================================================================
  useEffect(() => {
    if (!active) return;
    
    if (GLOBAL_HAS_LOADED) {
      console.log('📦 Using global cache on mount (hasLoaded=true)');
      setHousehold(GLOBAL_HOUSEHOLD_CACHE);
      setHouseholdUsers(GLOBAL_USERS_CACHE);
      setStats(GLOBAL_STATS_CACHE);
      setFamilyMembers(GLOBAL_MEMBERS_CACHE); // ✅ v28.4: Members aus Cache
      setIsLoading(false);
      setIsInitialLoad(false);
      return;
    }
    
    if (isAuthLoading || tokenLoading) {
      console.log('⏳ Auth/Token loading...');
      return;
    }
    
    if (!isAuthenticated) {
      console.log('🔒 Not authenticated');
      setIsLoading(false);
      setIsInitialLoad(false);
      return;
    }
    
    // ✅ v26: Warten auf Token aus Context
    if (!token) {
      console.log('⏳ Waiting for token from TokenContext...');
      return;
    }
    
    console.log('🎬 Starting initial load (no cache)');
    loadData();
  }, [active, isAuthenticated, isAuthLoading, tokenLoading, token, loadData]);

  // ============================================================================
  // REFRESH HANDLERS
  // ============================================================================
  const handleSilentRefresh = useCallback(() => {
    console.log('🔄 Silent refresh');
    
    trackEvent('household_refresh', { type: 'silent' });
    
    GLOBAL_HAS_LOADED = false;
    GLOBAL_LOADING_PROMISE = null;
    loadData(true);
  }, [loadData, trackEvent]);

  const handleRetry = useCallback(() => {
    console.log('🔄 Manual retry');
    
    trackEvent('household_retry', { type: 'manual' });
    
    GLOBAL_HAS_LOADED = false;
    GLOBAL_LOADING_PROMISE = null;
    setError(null);
    setIsInitialLoad(true);
    loadData(true);
  }, [loadData, trackEvent]);

  // ✅ v26: refreshData verwendet Token aus Context
  const refreshData = useCallback(async () => {
    if (!token) {
      console.warn('❌ No token available for refresh');
      return;
    }
    
    try {
      const result = await fetchDataFromBackend();
      
      GLOBAL_HOUSEHOLD_CACHE = result.household;
      GLOBAL_USERS_CACHE = result.users;
      GLOBAL_STATS_CACHE = result.stats;
      
      if (isMountedRef.current) {
        setHousehold(result.household);
        setHouseholdUsers(result.users);
        setStats(result.stats);
      }
    } catch (err) {
      console.error('Error refreshing data:', err);
    }
  }, [fetchDataFromBackend, token]);

  // Clear messages after 5s
  useEffect(() => {
    if (actionSuccess || actionError) {
      const timer = setTimeout(() => {
        setActionSuccess(null);
        setActionError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [actionSuccess, actionError]);

  // ============================================
  // ✅ v26: SHARING HANDLERS - Mit Token aus Context
  // ============================================
  
  const handleCreateHousehold = async (e) => {
    e.preventDefault();
    if (!householdName.trim() || actionLoading) return;
    
    if (!token) {
      setActionError('Kein Token verfügbar');
      return;
    }
    
    try {
      setActionLoading(true);
      setActionError(null);
      
      const result = await apiCall('/api/household', {
        method: 'POST',
        body: JSON.stringify({ name: householdName.trim() })
      });
      
      trackEvent('household_create', { 
        household_name: householdName.trim(),
        migrated_lists: result.migrated?.lists || 0
      });
      
      setHousehold(result.household);
      setShowCreateForm(false);
      setHouseholdName('');
      setActionSuccess(`Haushalt "${result.household.name}" erstellt! ${result.migrated?.lists || 0} Listen migriert.`);
      await refreshData();
      
    } catch (err) {
      trackEvent('household_create_error', { error: err.message });
      setActionError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleJoinHousehold = async (e) => {
    e.preventDefault();
    if (joinCode.length !== 8 || actionLoading) return;
    
    if (!token) {
      setActionError('Kein Token verfügbar');
      return;
    }
    
    try {
      setActionLoading(true);
      setActionError(null);
      
      const result = await apiCall('/api/household/join', {
        method: 'POST',
        body: JSON.stringify({ code: joinCode.trim().toUpperCase() })
      });
      
      trackEvent('household_join', { 
        household_name: result.household.name
      });
      
      setHousehold(result.household);
      setShowJoinForm(false);
      setJoinCode('');
      setActionSuccess(`Du bist "${result.household.name}" beigetreten!`);
      await refreshData();
      
    } catch (err) {
      trackEvent('household_join_error', { error: err.message });
      setActionError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleLeaveHousehold = async () => {
    const confirmMsg = household?.is_owner && householdUsers.length > 1
      ? 'Als Ersteller kannst du nur verlassen wenn du das letzte Mitglied bist.'
      : 'Haushalt wirklich verlassen? Deine eigenen Daten bleiben erhalten.';
    
    if (!window.confirm(confirmMsg)) return;
    
    if (!token) {
      setActionError('Kein Token verfügbar');
      return;
    }
    
    try {
      setActionLoading(true);
      const result = await apiCall('/api/household/leave', { method: 'POST' });
      
      trackEvent('household_leave', { 
        household_name: household?.name
      });
      
      GLOBAL_HOUSEHOLD_CACHE = null;
      GLOBAL_USERS_CACHE = [];
      
      setHousehold(null);
      setHouseholdUsers([]);
      setActionSuccess(result.message);
    } catch (err) {
      trackEvent('household_leave_error', { error: err.message });
      setActionError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveUser = async (user) => {
    if (!window.confirm(`"${user.display_name}" wirklich aus dem Haushalt entfernen?`)) return;
    
    if (!token) {
      setActionError('Kein Token verfügbar');
      return;
    }
    
    try {
      setActionLoading(true);
      await apiCall(`/api/household/users/${user.id}`, { method: 'DELETE' });
      
      trackEvent('household_user_remove', { 
        removed_user_role: user.role
      });
      
      setActionSuccess(`${user.display_name} wurde entfernt`);
      await refreshData();
    } catch (err) {
      trackEvent('household_user_remove_error', { error: err.message });
      setActionError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeRole = async (user, newRole) => {
    if (!token) {
      setActionError('Kein Token verfügbar');
      return;
    }
    
    try {
      setActionLoading(true);
      await apiCall(`/api/household/users/${user.id}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole })
      });
      
      trackEvent('household_role_change', { 
        old_role: user.role,
        new_role: newRole
      });
      
      setActionSuccess(`${user.display_name} ist jetzt ${newRole === 'admin' ? 'Admin' : 'Mitglied'}`);
      await refreshData();
    } catch (err) {
      trackEvent('household_role_change_error', { error: err.message });
      setActionError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // ============================================
  // RENDER GUARDS
  // ============================================
  if (!active) return null;

  // ============================================================================
  // RENDER: SKELETON UI - ✅ v26: tokenLoading hinzugefügt
  // ============================================================================
  if ((isInitialLoad || tokenLoading) && !GLOBAL_HAS_LOADED && !error) {
    return (
      <div style={styles.container}>
        <div className="household-header-row1">
          <div className="household-header-left">
            <Skeleton width={44} height={44} borderRadius={12} />
            <div className="household-header-title-wrap">
              <Skeleton width={150} height={20} style={{ marginBottom: 6 }} />
              <Skeleton width={100} height={14} />
            </div>
          </div>
          <Skeleton width={44} height={44} borderRadius={12} />
        </div>
        
        <div style={styles.content}>
          <Skeleton width="100%" height={200} borderRadius={16} style={{ marginBottom: 16 }} />
          <Skeleton width="100%" height={150} borderRadius={16} style={{ marginBottom: 16 }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            <Skeleton width="100%" height={100} borderRadius={12} />
            <Skeleton width="100%" height={100} borderRadius={12} />
            <Skeleton width="100%" height={100} borderRadius={12} />
          </div>
        </div>
      </div>
    );
  }

  // Auth Loading
  if (isAuthLoading) {
    return (
      <div style={styles.container}>
        <Loading icon="🔐" text="Authentifizierung..." />
      </div>
    );
  }

  // Error
  if (error && !GLOBAL_HAS_LOADED) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <span style={{ fontSize: '3rem' }}>⚠️</span>
          <p style={{ margin: 0, fontWeight: 600 }}>{error}</p>
          <button style={styles.retryBtn} onClick={handleRetry}>
            🔄 {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: NO HOUSEHOLD
  // ============================================
  if (!household) {
    return (
      <div style={styles.container}>
        {showLoading && (
          <div style={styles.loadingIndicator}>
            <span style={{ animation: 'pulse 1s infinite' }}>⏳</span>
            <span>Aktualisiere...</span>
          </div>
        )}

        {/* HEADER */}
        <div className="household-header-row1">
          <div className="household-header-left">
            <div className="household-header-icon">👥</div>
            <div className="household-header-title-wrap">
              <h1 className="household-header-title">{t('household.title')}</h1>
              <span className="household-header-subtitle">
                {familyMembers.length} Mitglieder
                {isLoading && !showLoading && (
                  <span className="household-sync-indicator">
                    <span className="household-sync-dot" />
                    sync
                  </span>
                )}
              </span>
            </div>
            <button
              className="household-info-btn"
              onClick={() => setShowInfo(!showInfo)}
              title="Modul-Info"
            >
              ⓘ
            </button>
          </div>
        </div>

        {/* Info Modal */}
        {showInfo && (
          <div style={styles.infoModal}>
            <div style={styles.infoModalHeader}>
              <span style={styles.infoModalTitle}>👥 Haushalt - Info</span>
              <button className="household-close-mini" style={styles.infoCloseBtn} onClick={() => setShowInfo(false)}>✕</button>
            </div>
            <div style={styles.infoModalContent}>
              <p style={styles.infoText}>
                <strong>Haushalt-Modul</strong> ermöglicht dir das Verwalten deiner Familie
                und das Teilen von Daten wie Kalender, Einkaufslisten und Aufgaben.
              </p>
              <ul style={styles.infoList}>
                <li>Erstelle einen gemeinsamen Haushalt</li>
                <li>Lade Familienmitglieder per Code ein</li>
                <li>Füge Kinder und Großeltern hinzu (ohne eigenen Account)</li>
                <li>Verwalte Rollen und Berechtigungen</li>
                <li>Sieh Aktivitäten aller Mitglieder</li>
                <li>Teile Kalender, Listen und Aufgaben</li>
              </ul>
            </div>
          </div>
        )}

        {/* CONTENT */}
        <div style={{
          ...styles.content,
          opacity: showLoading ? 0.7 : 1,
          transition: 'opacity 0.2s ease',
          pointerEvents: showLoading ? 'none' : 'auto'
        }}>
          {actionError && (
            <div style={styles.errorBanner}>❌ {actionError}</div>
          )}
          
          <div style={styles.emptyHouseholdContainer}>
            <div style={styles.emptyIcon}>🏠</div>
            <h3 style={styles.emptyTitle}>Noch kein geteilter Haushalt</h3>
            <p style={styles.emptyText}>
              Erstelle einen Haushalt und lade Familienmitglieder ein, 
              oder tritt einem bestehenden Haushalt bei.
            </p>
            
            {showCreateForm ? (
              <form onSubmit={handleCreateHousehold} style={styles.formCard}>
                <h4 style={styles.formTitle}>➕ Haushalt erstellen</h4>
                <input
                  type="text"
                  value={householdName}
                  onChange={(e) => setHouseholdName(e.target.value)}
                  placeholder="z.B. Familie Müller"
                  style={styles.input}
                  maxLength={50}
                  autoFocus
                />
                <div style={styles.formButtons}>
                  <button
                    type="submit"
                    disabled={!householdName.trim() || householdName.trim().length < 2 || actionLoading}
                    style={{
                      ...styles.primaryBtn,
                      opacity: (!householdName.trim() || householdName.trim().length < 2) ? 0.5 : 1
                    }}
                  >
                    {actionLoading ? '⏳ Erstelle...' : '✅ Erstellen'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowCreateForm(false); setHouseholdName(''); }}
                    style={styles.secondaryBtn}
                  >
                    Abbrechen
                  </button>
                </div>
              </form>
            ) : showJoinForm ? (
              <form onSubmit={handleJoinHousehold} style={styles.formCard}>
                <h4 style={styles.formTitle}>🔗 Haushalt beitreten</h4>
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                  placeholder="ABCD1234"
                  style={styles.codeInput}
                  maxLength={8}
                  autoFocus
                />
                <p style={styles.hint}>Frag ein Familienmitglied nach dem 8-stelligen Code</p>
                <div style={styles.formButtons}>
                  <button
                    type="submit"
                    disabled={joinCode.length !== 8 || actionLoading}
                    style={{
                      ...styles.primaryBtn,
                      opacity: joinCode.length !== 8 ? 0.5 : 1
                    }}
                  >
                    {actionLoading ? '⏳ Trete bei...' : '🔗 Beitreten'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowJoinForm(false); setJoinCode(''); }}
                    style={styles.secondaryBtn}
                  >
                    Abbrechen
                  </button>
                </div>
              </form>
            ) : (
              <div style={styles.actionButtons}>
                <button
                  onClick={() => setShowCreateForm(true)}
                  style={styles.primaryBtn}
                >
                  ➕ Haushalt erstellen
                </button>
                <button
                  onClick={() => setShowJoinForm(true)}
                  style={styles.secondaryBtn}
                >
                  🔗 Mit Code beitreten
                </button>
              </div>
            )}
          </div>
          
          {/* STATS */}
          {stats && (
            <div style={styles.statsSection}>
              <h3 style={styles.statsSectionTitle}>📊 Deine Daten (noch nicht geteilt)</h3>
              <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                  <div style={styles.statIcon}>🛒</div>
                  <p style={styles.statValue}>{stats.shoppingLists || 0}</p>
                  <p style={styles.statLabel}>Einkaufslisten</p>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statIcon}>🍳</div>
                  <p style={styles.statValue}>{stats.recipes || 0}</p>
                  <p style={styles.statLabel}>Rezepte</p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </div>
    );
  }

  // ============================================
  // RENDER: HAS HOUSEHOLD - ✅ v28: MASTER-DETAIL LAYOUT
  // ============================================
  return (
    <div style={styles.container}>
      {showLoading && (
        <div style={styles.loadingIndicator}>
          <span style={{ animation: 'pulse 1s infinite' }}>⏳</span>
          <span>Aktualisiere...</span>
        </div>
      )}

      {/* HEADER */}
      <div style={styles.headerRow}>
        <div style={styles.headerLeft}>
          <div style={styles.headerIcon}>👥</div>
          <div style={{ flex: 1 }}>
            <h1 style={styles.headerTitle}>{household.name}</h1>
            <p style={styles.headerSubtitle}>
              {household.member_count} {household.member_count === 1 ? 'Mitglied' : 'Mitglieder'}
              {isLoading && !showLoading && (
                <span style={styles.syncIndicator}>
                  <span style={styles.syncDot} />
                  sync
                </span>
              )}
            </p>
          </div>
          <button
            style={styles.infoBtn}
            onClick={() => setShowInfo(!showInfo)}
            title="Modul-Info"
          >
            ⓘ
          </button>
        </div>
      </div>

      {/* Info Modal */}
      {showInfo && (
        <div style={styles.infoModal}>
          <div style={styles.infoModalHeader}>
            <span style={styles.infoModalTitle}>👥 Haushalt - Info</span>
            <button className="household-close-mini" style={styles.infoCloseBtn} onClick={() => setShowInfo(false)}>✕</button>
          </div>
          <div style={styles.infoModalContent}>
            <p style={styles.infoText}>
              <strong>Haushalt-Modul</strong> ermöglicht dir das Verwalten deiner Familie
              und das Teilen von Daten wie Kalender, Einkaufslisten und Aufgaben.
            </p>
            <ul style={styles.infoList}>
              <li>Erstelle einen gemeinsamen Haushalt</li>
              <li>Lade Familienmitglieder per Code ein</li>
              <li>Füge Kinder und Großeltern hinzu (ohne eigenen Account)</li>
              <li>Verwalte Rollen und Berechtigungen</li>
              <li>Sieh Aktivitäten aller Mitglieder</li>
              <li>Teile Kalender, Listen und Aufgaben</li>
            </ul>
          </div>
        </div>
      )}

      {/* Messages */}
      {actionError && (
        <div style={styles.errorBanner}>❌ {actionError}</div>
      )}
      {actionSuccess && (
        <div style={styles.successBanner}>✅ {actionSuccess}</div>
      )}

      {/* ✅ v28: MASTER-DETAIL LAYOUT */}
      <div style={styles.masterDetailContainer}>
        {/* LEFT: Family Member List */}
        <div style={{
          ...styles.listPanel,
          display: showMobileDetail ? 'none' : 'flex',
          ...(window.innerWidth > 768 ? { display: 'flex' } : {})
        }}>
          <FamilyMemberList
            members={familyMembers}
            selectedMemberId={selectedMember?.id}
            onSelectMember={handleSelectMember}
            onAddMember={() => setShowAddMemberModal(true)}
            onInviteWithCode={household?.my_role === 'admin' ? handleInviteWithCode : null}
            loading={isLoading}
          />

          {/* Haushalt-Aktionen unter der Liste */}
          <div style={styles.householdActions}>
            <div style={styles.householdInfoCompact}>
              <span style={styles.householdNameSmall}>🏠 {household.name}</span>
              <span style={household.my_role === 'admin' ? styles.adminBadgeSmall : styles.memberBadgeSmall}>
                {household.my_role === 'admin' ? '👑 Admin' : '👤 Mitglied'}
              </span>
            </div>
            <button
              onClick={handleLeaveHousehold}
              style={styles.leaveBtn}
              disabled={actionLoading}
            >
              🚪 Verlassen
            </button>
          </div>
        </div>

        {/* RIGHT: Family Member Detail */}
        <div style={{
          ...styles.detailPanel,
          display: showMobileDetail || window.innerWidth > 768 ? 'flex' : 'none'
        }}>
          <FamilyMemberDetail
            member={selectedMember}
            onBack={window.innerWidth <= 768 ? handleBackToList : null}
            onEdit={selectedMember?.isMember ? handleOpenEditMember : null} // ✅ v28.5: Nur für Members (nicht für User mit Account)
            onNavigate={(item, moduleName) => {
              // Navigiere zum entsprechenden Modul
              if (onNavigate && moduleName) {
                console.log('👥 Navigating to:', moduleName, item ? `(item: ${item.id})` : '(all)');
                onNavigate(moduleName);
              }
            }}
          />
        </div>
      </div>

      {/* ✅ v28.2: Add Member Modal */}
      <AddMemberModal
        isOpen={showAddMemberModal}
        onClose={() => setShowAddMemberModal(false)}
        onSave={handleAddMember}
        isLoading={addMemberLoading}
      />

      {/* ✅ v28.5: Edit Member Modal */}
      <EditMemberModal
        isOpen={showEditMemberModal}
        onClose={() => {
          setShowEditMemberModal(false);
          setMemberToEdit(null);
        }}
        onSave={handleEditMember}
        onDelete={handleDeleteMember}
        member={memberToEdit}
        isLoading={editMemberLoading}
      />

      {/* ✅ v28.3: Invite Code Modal */}
      {showInviteCodeModal && (
        <div style={styles.modalOverlay} onClick={() => setShowInviteCodeModal(false)}>
          <div style={styles.inviteCodeModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.inviteCodeHeader}>
              <h3 style={styles.inviteCodeTitle}>🔗 Einladungscode</h3>
              <button
                className="household-close-mini"
                style={styles.closeModalBtn}
                onClick={() => setShowInviteCodeModal(false)}
              >
                ✕
              </button>
            </div>

            <p style={styles.inviteCodeDescription}>
              Teile diesen Code mit Familienmitgliedern, um sie zu deinem Haushalt einzuladen.
            </p>

            <div style={styles.codeDisplay}>
              <span style={styles.codeText}>{household?.invite_code || '--------'}</span>
            </div>

            <div style={styles.inviteCodeActions}>
              <button
                style={styles.copyCodeBtn}
                onClick={handleCopyInviteCode}
              >
                📋 Kopieren
              </button>
              <button
                style={styles.shareCodeBtn}
                onClick={handleShareInviteCode}
              >
                📤 Teilen
              </button>
            </div>

            <p style={styles.inviteCodeHint}>
              Der Code ist unbegrenzt gültig und kann mehrfach verwendet werden.
            </p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}

// ============================================================================
// STYLES
// ============================================================================
function getStyles(isDark, isMobile = false) {
  const colors = isDark ? {
    bg: '#111827',
    bgCard: '#1F2937',
    bgSecondary: '#374151',
    text: '#F9FAFB',
    textSecondary: '#D1D5DB',
    textMuted: '#9CA3AF',
    border: '#4B5563',
    accent: '#4A90A4',
    accentLight: '#6BA893'
  } : {
    bg: '#E8F4F8',
    bgCard: '#FFFFFF',
    bgSecondary: '#E8F4F8',
    text: '#1F2937',
    textSecondary: '#374151',
    textMuted: '#6B7280',
    border: '#B8D4DC',
    accent: '#4A90A4',
    accentLight: '#6BA893'
  };

  return {
    container: {
      padding: isMobile ? '5px' : '2rem 1.25rem',
      maxWidth: isMobile ? '100%' : '1400px',
      margin: '0 auto',
      width: '100%',
      background: colors.bg,
      minHeight: 'calc(100vh - 100px)',
      borderRadius: isMobile ? '0' : '20px',
      boxSizing: 'border-box',
      overflowX: 'hidden'
    },
    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: isMobile ? '0.75rem' : '1.5rem',
      padding: isMobile ? '0.75rem' : '0',
      flexWrap: 'wrap',
      gap: isMobile ? '0.75rem' : '1rem'
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '10px' : '12px',
      flex: 1
    },
    headerIcon: {
      width: isMobile ? '44px' : '48px',
      height: isMobile ? '44px' : '48px',
      background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
      borderRadius: isMobile ? '10px' : '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? '1.3rem' : '1.5rem',
      boxShadow: '0 4px 12px rgba(74, 144, 164, 0.3)'
    },
    headerTitle: {
      margin: 0,
      color: colors.text,
      fontSize: isMobile ? '1.3rem' : '1.5rem',
      fontWeight: 700
    },
    headerSubtitle: {
      margin: 0,
      color: colors.textMuted,
      fontSize: '0.875rem',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    syncIndicator: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '0.75rem',
      color: '#4A90A4',
      marginLeft: '8px'
    },
    syncDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: '#4A90A4',
      animation: 'pulse 1s infinite'
    },
    // ✅ v29.2: Info Button — Kalender-Design (44px Desktop, 32px Mobile)
    infoBtn: {
      width: isMobile ? '32px' : '44px',
      height: isMobile ? '32px' : '44px',
      background: isMobile ? 'transparent' : (isDark ? colors.bgCard : '#F8FAFC'),
      border: isMobile ? 'none' : `1px solid ${isDark ? colors.border : '#E2E8F0'}`,
      borderRadius: '12px',
      fontSize: isMobile ? '1rem' : '1.25rem',
      color: isDark ? colors.textMuted : '#475569',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s',
      marginLeft: isMobile ? '0.25rem' : '0.5rem',
      flexShrink: 0,
      boxSizing: 'border-box',
      position: 'relative',
      padding: 0,
      lineHeight: 1
    },
    infoModal: {
      background: colors.bgCard,
      border: `2px solid ${colors.border}`,
      borderRadius: '16px',
      marginBottom: '1rem',
      boxShadow: '0 4px 20px rgba(74, 144, 164, 0.15)',
      overflow: 'hidden'
    },
    infoModalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem 1.25rem',
      background: 'linear-gradient(135deg, #4A90A4, #6BA893)',
      color: 'white'
    },
    infoModalTitle: {
      fontSize: '1rem',
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    infoCloseBtn: {
      background: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      borderRadius: '8px',
      width: '32px',
      height: '32px',
      fontSize: '1rem',
      color: 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s',
      position: 'relative'
    },
    infoModalContent: {
      padding: '1.25rem'
    },
    infoText: {
      margin: '0 0 1rem 0',
      color: '#4B5563',
      fontSize: '0.9rem',
      lineHeight: 1.6
    },
    infoList: {
      margin: 0,
      padding: '0 0 0 1.25rem',
      color: '#4B5563',
      fontSize: '0.85rem',
      lineHeight: 1.8
    },
    loadingIndicator: {
      position: 'fixed',
      top: '80px',
      right: '20px',
      background: 'rgba(74, 144, 164, 0.95)',
      color: 'white',
      padding: '8px 16px',
      borderRadius: '20px',
      fontSize: '0.85rem',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      zIndex: 1000,
      boxShadow: '0 4px 12px rgba(74, 144, 164, 0.3)'
    },
    content: {
      background: colors.bgCard,
      borderRadius: '16px',
      border: `2px solid ${colors.border}`,
      padding: '1.5rem',
      boxShadow: isDark ? '0 4px 20px rgba(0, 0, 0, 0.3)' : '0 4px 20px rgba(74, 144, 164, 0.1)'
    },

    // ✅ v28: Master-Detail Layout
    // Note: @media queries are handled in Household.css
    masterDetailContainer: {
      display: 'grid',
      gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 2fr',
      gap: '1.5rem',
      minHeight: 'calc(100vh - 200px)'
    },
    listPanel: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      minWidth: 0
    },
    detailPanel: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0
    },
    householdActions: {
      background: colors.bgCard,
      borderRadius: '12px',
      border: `2px solid ${colors.border}`,
      padding: '12px 16px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '12px'
    },
    householdInfoCompact: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    },
    householdNameSmall: {
      fontSize: '0.9rem',
      fontWeight: 600,
      color: colors.text
    },
    adminBadgeSmall: {
      fontSize: '0.75rem',
      color: '#D97706',
      fontWeight: 500
    },
    memberBadgeSmall: {
      fontSize: '0.75rem',
      color: colors.accent,
      fontWeight: 500
    },
    leaveBtn: {
      padding: '0 20px',
      minHeight: '44px',
      background: isDark ? '#7F1D1D' : '#FEF2F2',
      color: '#DC2626',
      border: `2px solid ${isDark ? '#DC2626' : '#FECACA'}`,
      borderRadius: '12px',
      fontSize: '0.9rem',
      fontWeight: 600,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem'
    },

    // Empty State
    emptyHouseholdContainer: {
      textAlign: 'center',
      padding: '2rem',
      background: isDark ? '#374151' : '#F0F9FF',
      borderRadius: '16px',
      border: `2px dashed ${colors.border}`
    },
    emptyIcon: { fontSize: '4rem', marginBottom: '1rem' },
    emptyTitle: {
      fontSize: '1.25rem',
      fontWeight: 700,
      color: colors.text,
      margin: '0 0 0.5rem 0'
    },
    emptyText: {
      color: colors.textMuted,
      marginBottom: '1.5rem',
      maxWidth: '400px',
      margin: '0 auto 1.5rem auto'
    },
    actionButtons: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
      flexWrap: 'wrap'
    },
    primaryBtn: {
      padding: '0 28px',
      minHeight: '52px',
      background: 'linear-gradient(135deg, #4A90A4, #6BA893)',
      color: 'white',
      border: 'none',
      borderRadius: '14px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      boxShadow: '0 4px 16px rgba(74, 144, 164, 0.25)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem'
    },
    secondaryBtn: {
      padding: '0 20px',
      minHeight: '44px',
      background: isDark ? '#374151' : 'white',
      color: colors.accent,
      border: `2px solid ${colors.border}`,
      borderRadius: '12px',
      fontSize: '0.9rem',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem'
    },
    formCard: {
      background: colors.bgCard,
      padding: '1.5rem',
      borderRadius: '12px',
      border: `2px solid ${colors.border}`,
      textAlign: 'left',
      maxWidth: '400px',
      margin: '0 auto'
    },
    formTitle: { margin: '0 0 1rem 0', color: colors.text, fontSize: '1.1rem' },
    input: {
      width: '100%',
      padding: '0.875rem 1rem',
      border: `2px solid ${colors.border}`,
      borderRadius: '12px',
      fontSize: '1rem',
      outline: 'none',
      background: isDark ? '#374151' : '#F0F9FF',
      color: colors.text,
      boxSizing: 'border-box',
      marginBottom: '0.5rem'
    },
    codeInput: {
      width: '100%',
      padding: '1rem',
      border: `2px solid ${colors.border}`,
      borderRadius: '12px',
      fontSize: '1.5rem',
      fontFamily: 'monospace',
      letterSpacing: '4px',
      textAlign: 'center',
      textTransform: 'uppercase',
      outline: 'none',
      background: isDark ? '#374151' : '#F0F9FF',
      color: colors.text,
      boxSizing: 'border-box'
    },
    hint: { fontSize: '0.8rem', color: colors.textMuted, margin: '0.5rem 0 1rem 0' },
    formButtons: { display: 'flex', gap: '0.75rem', marginTop: '1rem' },
    
    // Household Card
    householdCard: {
      background: isDark ? '#374151' : '#E8F4F8',
      borderRadius: '16px',
      border: `2px solid ${colors.border}`,
      padding: '1.5rem',
      marginBottom: '1.5rem'
    },
    householdHeader: { display: 'flex', alignItems: 'center', gap: '1rem' },
    householdIconLarge: {
      width: '56px',
      height: '56px',
      background: 'linear-gradient(135deg, #4A90A4, #6BA893)',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.75rem'
    },
    householdInfo: { flex: 1 },
    householdName: { margin: '0 0 0.25rem 0', fontSize: '1.25rem', fontWeight: 700, color: colors.text },
    householdMeta: { display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' },
    memberCount: { fontSize: '0.9rem', color: colors.textMuted },
    adminBadge: {
      padding: '4px 10px',
      background: '#FEF3C7',
      borderRadius: '6px',
      fontSize: '0.8rem',
      fontWeight: 600,
      color: '#D97706'
    },
    memberBadge: {
      padding: '4px 10px',
      background: isDark ? '#1E3A8A' : '#E0E7FF',
      borderRadius: '6px',
      fontSize: '0.8rem',
      fontWeight: 600,
      color: isDark ? '#93C5FD' : '#4F46E5'
    },
    ownerBadge: {
      padding: '4px 10px',
      background: isDark ? '#064E3B' : '#D1FAE5',
      borderRadius: '6px',
      fontSize: '0.8rem',
      fontWeight: 600,
      color: isDark ? '#6EE7B7' : '#065F46'
    },
    
    // Section
    sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
    sectionTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '1.1rem',
      fontWeight: 700,
      color: colors.text,
      margin: 0
    },
    
    // Users List
    usersList: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
    userCard: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem',
      background: colors.bgCard,
      borderRadius: '12px',
      border: `1px solid ${colors.border}`
    },
    userInfo: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
    userAvatar: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #4A90A4, #6BA893)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.25rem'
    },
    userDetails: { minWidth: 0 },
    userName: { fontWeight: 600, color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' },
    userEmail: { fontSize: '0.85rem', color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis' },
    youTag: { fontSize: '0.75rem', color: colors.accent, fontWeight: 500 },
    ownerTag: {
      padding: '4px 8px',
      background: '#FEF3C7',
      borderRadius: '6px',
      fontSize: '0.75rem',
      color: '#D97706',
      fontWeight: 500
    },
    userActions: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
    roleSelect: {
      padding: '0.4rem 0.5rem',
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      fontSize: '0.85rem',
      cursor: 'pointer',
      background: colors.bgCard,
      color: colors.text
    },
    removeUserBtn: {
      padding: 0,
      width: '32px',
      height: '32px',
      background: isDark ? '#7F1D1D' : '#FEF2F2',
      border: `1px solid ${isDark ? '#DC2626' : '#FECACA'}`,
      borderRadius: '8px',
      color: '#DC2626',
      cursor: 'pointer',
      fontSize: '0.9rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative'
    },
    
    // Stats
    statsSection: { marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: `1px solid ${colors.border}` },
    statsSectionTitle: { margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 600, color: colors.text },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem' },
    statCard: {
      background: isDark ? '#374151' : '#E8F4F8',
      border: `2px solid ${colors.border}`,
      borderRadius: '12px',
      padding: '1rem',
      textAlign: 'center'
    },
    statIcon: { fontSize: '1.5rem', marginBottom: '0.25rem' },
    statValue: { fontSize: '1.5rem', fontWeight: 700, color: colors.accent, margin: '0 0 0.25rem 0' },
    statLabel: { fontSize: '0.8rem', color: colors.textMuted, margin: 0 },
    
    // Danger
    dangerSection: {
      borderTop: `1px solid ${colors.border}`,
      paddingTop: '1.5rem',
      marginTop: '1.5rem',
      textAlign: 'center'
    },
    dangerBtn: {
      padding: '0 20px',
      minHeight: '44px',
      background: isDark ? '#7F1D1D' : 'white',
      color: '#DC2626',
      border: `2px solid ${isDark ? '#DC2626' : '#FECACA'}`,
      borderRadius: '12px',
      fontSize: '0.9rem',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem'
    },
    dangerHint: { fontSize: '0.8rem', color: colors.textMuted, marginTop: '0.5rem' },
    
    // Messages
    errorBanner: {
      padding: '1rem',
      background: isDark ? '#7F1D1D' : '#FEF2F2',
      border: `1px solid ${isDark ? '#DC2626' : '#FECACA'}`,
      borderRadius: '12px',
      color: isDark ? '#FCA5A5' : '#991B1B',
      marginBottom: '1rem'
    },
    successBanner: {
      padding: '1rem',
      background: isDark ? '#064E3B' : '#ECFDF5',
      border: `1px solid ${isDark ? '#10B981' : '#A7F3D0'}`,
      borderRadius: '12px',
      color: isDark ? '#6EE7B7' : '#065F46',
      marginBottom: '1rem'
    },
    errorContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      background: isDark ? '#450A0A' : '#FEF2F2',
      borderRadius: '16px',
      border: `2px solid ${isDark ? '#DC2626' : '#FECACA'}`,
      gap: '1rem',
      color: isDark ? '#FCA5A5' : '#991B1B'
    },
    retryBtn: {
      padding: '0 28px',
      minHeight: '52px',
      background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
      color: 'white',
      border: 'none',
      borderRadius: '14px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      boxShadow: '0 4px 16px rgba(74, 144, 164, 0.25)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem'
    },

    // ✅ v28.3: Invite Code Modal Styles
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    },
    inviteCodeModal: {
      background: colors.bgCard,
      borderRadius: '20px',
      border: `2px solid ${colors.border}`,
      padding: '2rem',
      maxWidth: '400px',
      width: '100%',
      boxShadow: isDark ? '0 20px 60px rgba(0, 0, 0, 0.5)' : '0 20px 60px rgba(74, 144, 164, 0.2)'
    },
    inviteCodeHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem'
    },
    inviteCodeTitle: {
      margin: 0,
      fontSize: '1.25rem',
      fontWeight: 700,
      color: colors.text
    },
    closeModalBtn: {
      background: 'transparent',
      border: 'none',
      fontSize: '1rem',
      cursor: 'pointer',
      color: colors.textMuted,
      padding: 0,
      width: '32px',
      height: '32px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative'
    },
    inviteCodeDescription: {
      color: colors.textMuted,
      fontSize: '0.9rem',
      margin: '0 0 1.5rem 0',
      lineHeight: 1.5
    },
    codeDisplay: {
      background: isDark ? '#374151' : '#E8F4F8',
      border: `2px dashed ${colors.accent}`,
      borderRadius: '12px',
      padding: '1.25rem',
      textAlign: 'center',
      marginBottom: '1.5rem'
    },
    codeText: {
      fontSize: '2rem',
      fontFamily: 'monospace',
      fontWeight: 700,
      letterSpacing: '4px',
      color: colors.accent
    },
    inviteCodeActions: {
      display: 'flex',
      gap: '0.75rem',
      marginBottom: '1rem'
    },
    copyCodeBtn: {
      flex: 1,
      padding: '0 20px',
      minHeight: '44px',
      background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: '0.9rem',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      boxShadow: '0 4px 16px rgba(74, 144, 164, 0.25)'
    },
    shareCodeBtn: {
      flex: 1,
      padding: '0 20px',
      minHeight: '44px',
      background: colors.bgCard,
      color: colors.accent,
      border: `2px solid ${colors.accent}`,
      borderRadius: '12px',
      fontSize: '0.9rem',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    inviteCodeHint: {
      color: colors.textMuted,
      fontSize: '0.8rem',
      margin: 0,
      textAlign: 'center'
    }
  };
}

export default Household;

